/*
 * Singurul loc care stie ca aplicatia poate rula si intr-o carcasa nativa.
 *
 * Restul codului intreaba de aici, nu importa pluginuri Capacitor direct. Doua
 * motive. Primul: pe web pluginurile nu trebuie sa ajunga in bundle deloc, iar
 * importul dinamic de mai jos face exact asta - `isNative` e fals, ramura nu se
 * atinge, iar codul nativ nu se descarca. Al doilea: cand mai apare o diferenta
 * intre web si telefon, se adauga aici, nu se imprastie prin ecrane.
 *
 * Regula pe care o respecta tot fisierul: pe web, fiecare functie se comporta
 * exact ca inainte de Capacitor. Aplicatia web e in productie; nimic de aici nu
 * are voie sa-i schimbe comportamentul.
 */

/*
 * Detectia se face pe globala injectata de puntea nativa, nu prin importul lui
 * `@capacitor/core`.
 *
 * Motivul e masurat, nu presupus: importul static aduce 10,7 KB in bundle-ul de
 * web, pentru un singur `true/false` care pe web e intotdeauna `false`.
 * Aplicatia web e in productie si nu are de ce sa plateasca pentru codul unei
 * carcase in care nu ruleaza.
 *
 * `window.Capacitor` e pus de runtime-ul nativ inainte sa se incarce aplicatia -
 * asa il gaseste si `@capacitor/core` insusi. In browser nu exista, deci
 * verificarea cade pe `false` fara sa arunce.
 */
declare global {
  interface Window {
    Capacitor?: { isNativePlatform?: () => boolean };
  }
}

/** Ruleaza intr-o carcasa nativa (Android/iOS), nu intr-un browser. */
export const isNative: boolean =
  typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.() === true;

/**
 * Adresa pe care o cere aplicatia nativa ca loc de intoarcere dupa SSO.
 *
 * Trebuie sa fie identica, caracter cu caracter, cu una din adresele din
 * SHOP_SSO_RETURN_URLS de pe portal: verificarea de acolo e `in_array` strict,
 * nu "incepe cu". O potrivire pe inceput ar fi un redirect deschis care cara un
 * bilet de autentificare, adica exact felul in care se fura sesiuni - motiv
 * pentru care nu incercam s-o facem mai permisiva.
 *
 * Pe web ramane null: acolo portalul foloseste adresa lui implicita, ca pana
 * acum, si nimic nu se schimba.
 */
export const nativeReturnUrl: string | null = isNative
  /*
   * `||`, nu `??`, si `.trim()` inainte.
   *
   * `??` prinde doar undefined si null, nu si sirul gol - iar sirul gol e exact
   * ce se obtine cand cineva decomenteaza VITE_NATIVE_RETURN_URL din .env si uita
   * sa-i puna valoare. Atunci aplicatia ar cere `return=` gol, portalul ar cadea
   * pe adresa lui implicita - una web - si autentificarea de pe telefon ar duce
   * intr-o pagina in loc sa se intoarca in aplicatie. Esec tacut, greu de cautat.
   */
  ? ((import.meta.env.VITE_NATIVE_RETURN_URL ?? '').trim() || 'optimumdesk-catalog://auth')
  : null;

/**
 * Deschide o adresa in afara aplicatiei.
 *
 * Pe web e `window.open`, ca pana acum. Intr-o carcasa nativa, un `target
 * ="_blank"` obisnuit nu duce nicaieri - sau, si mai rau, incarca pagina peste
 * aplicatie si omul ramane fara drum inapoi. `Browser.open` deschide un browser
 * in-app, cu buton de inchidere, si aplicatia ramane unde era.
 */
export async function openExternal(url: string): Promise<void> {
  if (!isNative) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }

  const { Browser } = await import('@capacitor/browser');
  await Browser.open({ url });
}

/**
 * Anunta cand sistemul deschide aplicatia printr-o adresa proprie.
 *
 * Asa se intoarce biletul de la SSO: browserul in-app ajunge pe
 * `optimumdesk-catalog://auth?ticket=...`, sistemul de operare recunoaste schema,
 * inchide browserul si trezeste aplicatia cu adresa aia. Pe web nu exista asa
 * ceva, deci functia nu face nimic si intoarce o dezabonare goala.
 */
export function onDeepLink(handler: (url: string) => void): () => void {
  if (!isNative) {
    return () => {};
  }

  let remove: (() => void) | null = null;
  let cancelled = false;

  void (async () => {
    const { App } = await import('@capacitor/app');
    const listener = await App.addListener('appUrlOpen', (event) => {
      handler(event.url);
    });

    // Dezabonarea putea fi ceruta inainte ca importul sa se termine.
    if (cancelled) {
      void listener.remove();
      return;
    }

    remove = () => {
      void listener.remove();
    };
  })();

  return () => {
    cancelled = true;
    if (remove) remove();
  };
}

/**
 * Butonul fizic de inapoi, pe Android.
 *
 * Fara asta, apasarea lui inchide aplicatia din orice ecran - inclusiv din
 * mijlocul unei cereri pe jumatate completata. Handler-ul primeste `canGoBack`
 * de la sistem si decide: mai e istoric, mergem inapoi; nu mai e, iesim.
 */
export function onBackButton(handler: (canGoBack: boolean) => void): () => void {
  if (!isNative) {
    return () => {};
  }

  let remove: (() => void) | null = null;
  let cancelled = false;

  void (async () => {
    const { App } = await import('@capacitor/app');
    const listener = await App.addListener('backButton', ({ canGoBack }) => {
      handler(canGoBack);
    });

    if (cancelled) {
      void listener.remove();
      return;
    }

    remove = () => {
      void listener.remove();
    };
  })();

  return () => {
    cancelled = true;
    if (remove) remove();
  };
}

/*
 * Blob -> base64 curat, fara prefixul `data:`.
 *
 * `writeFile` primeste binarul ca base64: pe nativ nu accepta Blob, si o spune
 * chiar in tipuri ("Blob data is only supported on Web"). Fara `encoding`,
 * pluginul decodeaza base64-ul si scrie octetii; cu `encoding` ar scrie chiar
 * sirul, adica un fisier cu base64 in el.
 *
 * `readAsDataURL` intoarce `data:application/pdf;base64,JVBER...`, deci se taie
 * pana dupa prima virgula. Se cauta virgula, nu se presupune lungimea
 * prefixului: acesta contine tipul MIME, care difera de la fisier la fisier.
 */
function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = () => reject(new Error('nu s-a putut citi fisierul'));
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const comma = result.indexOf(',');

      resolve(comma === -1 ? result : result.slice(comma + 1));
    };

    reader.readAsDataURL(blob);
  });
}

/**
 * Deschide un fisier in afara aplicatiei.
 *
 * ---------------------------------------------------------------------------
 * DE CE NU AJUNGE UN `target="_blank"`
 * ---------------------------------------------------------------------------
 *
 * Pe web, o adresa `blob:` deschisa in fila noua e exact ce trebuie: browserul
 * are vizualizator de PDF si bara lui de unelte. Pe telefon nu tine nimic din
 * asta. WebView-ul Android NU randeaza PDF-uri - nu are vizualizator, deci un
 * `<object type="application/pdf">` cade mereu pe continutul de rezerva - iar
 * adresa `blob:` traieste doar inauntrul paginii de pe `https://localhost`:
 * n-o vede nici browserul in-app, nici alta aplicatie. Butonul arata ca merge
 * si nu face nimic.
 *
 * Asa ca pe nativ fisierul se SCRIE pe disc si se da sistemului, care il
 * deschide cu aplicatia potrivita - cititorul de PDF, galeria. Omul ramane cu
 * butonul lui de inapoi in aplicatia noastra.
 *
 * `Directory.Cache`: e o copie de unica folosinta pentru vizualizare, nu un
 * fisier salvat de om. Sistemul are voie s-o stearga cand are nevoie de spatiu,
 * si asta e exact ce vrem - altfel fiecare fisier deschis ar ramane pe telefon
 * pentru totdeauna. Pluginul de deschidere isi declara singur `FileProvider`-ul
 * peste `cache-path`, deci nu trebuie nimic in manifestul nostru.
 *
 * Pretul, spus pe fata: base64-ul creste continutul cu o treime si trece prin
 * puntea JS->nativ ca un singur sir. Pe fisierele de aici - cel mult 10 MB, cat
 * accepta si formularul de incarcare - se simte ca o clipa de asteptare, nu ca
 * o problema. Pe fisiere mult mai mari ar fi trebuit scris in bucati.
 */
export async function openFile(blob: Blob, name: string): Promise<void> {
  if (!isNative) {
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');

    /*
     * Eliberarea NU se face imediat. Fila noua inca nu s-a deschis cand se
     * intoarce `window.open`, iar un `revokeObjectURL` pe loc i-ar trage
     * adresa de sub picioare si ar arata o pagina goala. Un minut e mult peste
     * cat ii trebuie sa incarce, si oricum totul se elibereaza la inchiderea
     * filei.
     */
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return;
  }

  const [{ Filesystem, Directory }, { FileOpener }] = await Promise.all([
    import('@capacitor/filesystem'),
    import('@capacitor-community/file-opener'),
  ]);

  /*
   * Numele vine din baza de date, deci poate contine orice a incarcat cineva.
   * Un `/` in el ar cere un subdirector care nu exista, iar `:` sau `?` sunt
   * nelegale pe unele sisteme de fisiere. Se inlocuiesc doar caracterele
   * periculoase - diacriticele si spatiile raman, ca omul sa recunoasca
   * fisierul in aplicatia care il deschide.
   */
  const safeName = name.replace(/[\\/:*?"<>|]/g, '_').trim() || 'fisier';

  const { uri } = await Filesystem.writeFile({
    path: safeName,
    data: await toBase64(blob),
    directory: Directory.Cache,
  });

  /*
   * `contentType` vine din raspunsul serverului, prin blob. Fara el, Android
   * ghiceste dupa extensie si uneori nu gaseste nicio aplicatie potrivita -
   * mai ales pentru fisiere fara extensie.
   */
  await FileOpener.open({ filePath: uri, contentType: blob.type });
}

/** Inchide browserul in-app, daca e deschis. Pe web, nimic. */
export async function closeInAppBrowser(): Promise<void> {
  if (!isNative) return;

  const { Browser } = await import('@capacitor/browser');
  try {
    await Browser.close();
  } catch {
    /* Nu era deschis. Nu e o eroare. */
  }
}
